import { z } from 'zod';

export const DataType = z.enum([
    'String',
    'Number',
    'Money',
    'Percent',
    'Boolean',
    'Date',
    'DateOnly',
    'MultilineText',
    'Json',
    'InheritedProduct',
    'Url',
    'Html',
    'StringArray'
]);
export type DataType = z.infer<typeof DataType>;

export const Weekday = z.enum(['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']);
export type Weekday = z.infer<typeof Weekday>;

export const InterestType = z.enum(['None', 'Simple', 'Amortized', 'Script']);
export type InterestType = z.infer<typeof InterestType>;

export const DayCountType = z.enum(['Thirty360', 'Thirty365', 'Actual360', 'Actual365', 'Actual']);
export type DayCountType = z.infer<typeof DayCountType>;

export const PaymentType = z.enum(['Fixed', 'Balance', 'Minimum', 'Script']);
export type PaymentType = z.infer<typeof PaymentType>;

export const ServicingMode = z.enum(['Live', 'Mirroring']);
export type ServicingMode = z.infer<typeof ServicingMode>;

export const LateFeeType = z.enum(['Fixed', 'Percentage', 'LesserOf', 'GreaterOf', 'Script', 'None']);
export type LateFeeType = z.infer<typeof LateFeeType>;

export const PortfolioType = z.enum(['LineOfCredit', 'Installment', 'Mortgage', 'Open', 'Revolving']);
export type PortfolioType = z.infer<typeof PortfolioType>;

export const CreditReportingMode = z.enum(['None', 'Concord', 'Client']);
export type CreditReportingMode = z.infer<typeof CreditReportingMode>;

export const AutopayBehavior = z.enum(['Optional', 'Required']);
export type AutopayBehavior = z.infer<typeof AutopayBehavior>;

export const ProductType = z.enum([
    'TimeshareLoan',
    'TimeshareMaintenance',
    'HomeImprovementLoan',
    'SolarLoan',
    'PowerPurchaseAgreement'
]);
export type ProductType = z.infer<typeof ProductType>;

export const PaymentCapStrategy = z.enum(['None', 'Payoff']);
export type PaymentCapStrategy = z.infer<typeof PaymentCapStrategy>;

export const AutopayAmountBasis = z.enum(['ScheduledPayment', 'TotalDue']);
export type AutopayAmountBasis = z.infer<typeof AutopayAmountBasis>;

export const SalesTaxStrategy = z.enum(['None', 'Combined', 'State', 'County', 'City', 'Special']);
export type SalesTaxStrategy = z.infer<typeof SalesTaxStrategy>;

export const PaymentDueDayRange = z.enum(['Days1To28', 'Days1To31']);
export type PaymentDueDayRange = z.infer<typeof PaymentDueDayRange>;

export const PaymentDueDayInvalidBehavior = z.enum(['LastDayOfMonth', 'FirstDayOfNextMonth']);
export type PaymentDueDayInvalidBehavior = z.infer<typeof PaymentDueDayInvalidBehavior>;

export const AccountType = z.enum([
    'Unsecured',
    'HomeImprovement',
    'InstallmentSalesContract',
    'Education',
    'Lease',
    'TimeShareLoan',
    'RealEstateMortgage',
    'CollectionAgency',
    'SecuredHomeImprovement',
    'NoteLoan',
    'RealEstateUnknown',
    'UtilityServices'
]);
export type AccountType = z.infer<typeof AccountType>;

export const CreditorClassification = z.enum([
    'Retail',
    'MedicalHealthCare',
    'OilCompany',
    'Government',
    'PersonalServices',
    'Insurance',
    'Educational',
    'Banking',
    'RentalLeasing',
    'Utilities',
    'CableCellular',
    'Financial',
    'CreditUnion',
    'Automotive',
    'CheckGuarantee'
]);
export type CreditorClassification = z.infer<typeof CreditorClassification>;

export const DataScope = z.enum([
    'Client',
    'ClientInherited',
    'Product',
    'ProductInherited',
    'Project',
    'ProjectInherited',
    'Obligation',
    'Lender',
    'Asset'
]);
export type DataScope = z.infer<typeof DataScope>;

export const DataScopeLabels: Record<DataScope, string> = {
    Client: 'Client',
    ClientInherited: 'Client Inherited',
    Product: 'Product',
    ProductInherited: 'Product Inherited',
    Project: 'Project',
    ProjectInherited: 'Project Inherited',
    Obligation: 'Obligation',
    Lender: 'Lender',
    Asset: 'Asset'
};

// Definitions authored at a higher scope are visible on that scope's own view
// alongside its same-scope definitions (e.g. Client + ClientInherited both show on the Client view).
export const InheritedScopes: Partial<Record<DataScope, DataScope>> = {
    Client: 'ClientInherited',
    Product: 'ProductInherited',
    Project: 'ProjectInherited'
};

export const ObligationStatusCategory = z.enum(['Pending', 'Open', 'Paused', 'Closed']);
export type ObligationStatusCategory = z.infer<typeof ObligationStatusCategory>;

export const ObligationBehavior = z.enum([
    'DontAcceptPayments',
    'DontProcessAutoPayments',
    'DontSendStatements',
    'DontChargeLateFees',
    'DontSendCommunications',
    'DontReportCredit',
    'DontCollect',
    'DontAllowCustomerPortal',
    'DontCreateInstallments'
]);
export type ObligationBehavior = z.infer<typeof ObligationBehavior>;

export const ObligationBehaviorLabels: Record<ObligationBehavior, string> = {
    DontAcceptPayments: "Don't Accept Payments",
    DontProcessAutoPayments: "Don't Process Auto Payments",
    DontSendStatements: "Don't Send Statements",
    DontChargeLateFees: "Don't Charge Late Fees",
    DontSendCommunications: "Don't Send Communications",
    DontReportCredit: "Don't Report Credit",
    DontCollect: "Don't Collect",
    DontAllowCustomerPortal: "Don't Allow Customer Portal",
    DontCreateInstallments: "Don't Create Installments"
};

export const ObligationBehaviorShortLabels: Record<ObligationBehavior, string> = {
    DontAcceptPayments: 'Accept Payments',
    DontProcessAutoPayments: 'Process Auto Payments',
    DontSendStatements: 'Send Statements',
    DontChargeLateFees: 'Charge Late Fees',
    DontSendCommunications: 'Send Communications',
    DontReportCredit: 'Report Credit',
    DontCollect: 'Collect',
    DontAllowCustomerPortal: 'Allow Customer Portal',
    DontCreateInstallments: 'Create Installments'
};

// Mirrors ObligationStatusCategoryBehavior on the server (the masks used to seed default statuses).
export const ObligationStatusCategoryDefaults: Record<ObligationStatusCategory, ObligationBehavior[]> = {
    Pending: [
        'DontAcceptPayments',
        'DontProcessAutoPayments',
        'DontSendStatements',
        'DontChargeLateFees',
        'DontSendCommunications',
        'DontReportCredit',
        'DontCollect',
        'DontCreateInstallments'
    ],
    Open: [],
    Paused: ['DontSendStatements'],
    Closed: [
        'DontAcceptPayments',
        'DontProcessAutoPayments',
        'DontSendStatements',
        'DontChargeLateFees',
        'DontSendCommunications',
        'DontReportCredit',
        'DontCollect',
        'DontCreateInstallments'
    ]
};

export const ObligorTypes = z.enum(['PrimaryObligor', 'Obligor', 'Trustee', 'Other']);
export type ObligorTypes = z.infer<typeof ObligorTypes>;

export const LedgerType = z.enum([
    'Asset',
    'UnappliedCash',
    'Impound',
    'Balance',
    'Interest',
    'Charges',
    'Meter',
    'Installment',
    'Principal'
]);
export type LedgerType = z.infer<typeof LedgerType>;

export const DataExportSource = z.enum([
    'Obligations',
    'Journals',
    'Obligors',
    'TreasurySummaries',
    'Transactions',
    'AccountHistory',
    'TrialBalance'
]);
export type DataExportSource = z.infer<typeof DataExportSource>;

export const DataExportType = z.enum(['Json', 'Xml', 'Csv', 'Excel', 'Txt']);
export type DataExportType = z.infer<typeof DataExportType>;

export const DataExportFieldDelimiter = z.enum(['Comma', 'Pipe', 'Caret', 'Tab']);
export type DataExportFieldDelimiter = z.infer<typeof DataExportFieldDelimiter>;

export const DataExportStatus = z.enum(['Pending', 'Processing', 'Complete', 'Failed']);
export type DataExportStatus = z.infer<typeof DataExportStatus>;

export enum KnownMetaKeys {
    Data = '@data'
}

export const StatementStatus = z.enum(['Unpaid', 'Partial', 'Paid']);
export type StatementStatus = z.infer<typeof StatementStatus>;

export const KnownDetailTypes = z.enum(['LineItem', 'Meter']);
export type KnownDetailTypes = z.infer<typeof KnownDetailTypes>;

export const ScriptType = z.enum([
    'CalculatedFields',
    'Function',
    'General',
    'InstallmentGeneration',
    'Journal',
    'Library',
    'Payment',
    'ReturnFee',
    'StatementGeneration',
    'Workflow'
]);
export type ScriptType = z.infer<typeof ScriptType>;

export const DocumentType = z.enum(['Statement', 'Transaction', 'Marketing', 'Collections', 'Attachment']);
export type DocumentType = z.infer<typeof DocumentType>;

export const BillingCategory = z.enum([
    'Statement',
    'LateCharge',
    'Collection',
    'AdvCollection',
    'AnnualStatement',
    'CustomerService',
    'WelcomeLetter',
    'DisconnectLetter',
    'OtherLetter'
]);
export type BillingCategory = z.infer<typeof BillingCategory>;

export const BillingCategoryLabels: Record<BillingCategory, string> = {
    Statement: 'Statement',
    LateCharge: 'Late Charge',
    Collection: 'Collection',
    AdvCollection: 'Advanced Collections',
    AnnualStatement: 'Annual Statement',
    CustomerService: 'Customer Service',
    WelcomeLetter: 'Welcome Letter',
    DisconnectLetter: 'Disconnect Letter',
    OtherLetter: 'Other Letter'
};

export const DeliveryMethod = z.enum(['Email', 'Sms', 'Paper']);
export type DeliveryMethod = z.infer<typeof DeliveryMethod>;

export const ListScriptQueryType = z.enum([
    'All',
    'RequiredScripts',
    'General',
    'Workflow',
    'Scheduled',
    'ObligationActions',
    'JournalActions',
    'PortalActions',
    'Library',
    'Function'
]);
export type ListScriptQueryType = z.infer<typeof ListScriptQueryType>;

export const ScriptSearchField = z.enum(['Name', 'Source']);
export type ScriptSearchField = z.infer<typeof ScriptSearchField>;

export const DataImportSearchField = z.enum(['Name', 'Mapping']);
export type DataImportSearchField = z.infer<typeof DataImportSearchField>;

export const DataExportSearchField = z.enum(['Name', 'Filter', 'Mapping']);
export type DataExportSearchField = z.infer<typeof DataExportSearchField>;

export const DocumentTemplateSearchField = z.enum(['Name', 'Mapping']);
export type DocumentTemplateSearchField = z.infer<typeof DocumentTemplateSearchField>;

export const ClientFilter = z.enum(['CurrentClient', 'AssociatedClients']);
export type ClientFilter = z.infer<typeof ClientFilter>;

export const BankAccountTypes = z.enum(['Checking', 'Savings']);
export type BankAccountTypes = z.infer<typeof BankAccountTypes>;

export const SortDirection = z.enum(['Ascending', 'Descending']);
export type SortDirection = z.infer<typeof SortDirection>;

export const PaymentMethod = z.enum(['Ach', 'Check', 'Card', 'Other', 'Direct', 'ClientIssued']);
export const PaymentMethods: Array<PaymentMethod> = ['Ach', 'Check', 'Card', 'Other', 'Direct', 'ClientIssued'];
export type PaymentMethod = z.infer<typeof PaymentMethod>;

export const PaymentMethodNames: Record<PaymentMethod, string> = {
    Ach: 'ACH',
    Check: 'Check',
    Card: 'Card',
    Other: 'Other',
    Direct: 'Direct',
    ClientIssued: 'Client Issued'
};

export const PaymentStatus = z.enum(['Pending', 'Posted', 'Failed', 'Canceled', 'Returned', 'Errored', 'Refunded']);
export type PaymentStatus = z.infer<typeof PaymentStatus>;

export const PaymentFrequency = z.enum(['once', 'monthly']);
export type PaymentFrequency = z.infer<typeof PaymentFrequency>;

export const PaymentChannel = z.enum([
    'AgentPortal',
    'CustomerPortal',
    'Mobile',
    'IVR',
    'Ebox',
    'Lockbox',
    'Cash',
    'AutoPay'
]);

export type PaymentChannel = z.infer<typeof PaymentChannel>;

export const PaymentChannelNames: Record<string, string> = {
    CustomerPortal: 'MAI Customer Portal',
    Mobile: 'Pay Mobile',
    IVR: 'IVR',
    AgentPortal: 'Agent Portal',
    Ebox: 'eBox',
    Lockbox: 'Lockbox',
    Cash: 'Cash',
    AutoPay: 'Autopay'
};

export const FeeType = z.enum(['Fixed', 'Percentage']);
export type FeeType = z.infer<typeof FeeType>;

export const NoteMode = z.enum(['Note', 'IncomingCall', 'OutgoingCall']);

export type NoteMode = z.infer<typeof NoteMode>;

export const Resolution = z.enum(['PromiseToPay', 'LeftMessage', 'ContactMade', 'NoAnswerOrCallBack', 'NotCalled']);

export type Resolution = z.infer<typeof Resolution>;

export const ResolutionNames: Record<string, string> = {
    PromiseToPay: 'Promise to Pay',
    LeftMessage: 'Left Message',
    ContactMade: 'Contact Made',
    NoAnswerOrCallBack: 'No Answer or Call Back',
    NotCalled: 'Not Called'
};

export const DelinquentReason = z.enum([
    'Unemployed',
    'LossOfIncomeFinancialHardship',
    'Divorce',
    'DisputeWithClientDissatisfiedWithResort',
    'RefusalToPay',
    'CustomerOversight',
    'Medical',
    'UnableToObtain',
    'ResearchCRFPending',
    'Bankrupt',
    'Deceased',
    'Other',
    'CCCS',
    'SettlementPending',
    'ReAgeProgram',
    'RefusedToState',
    'NoCorrespondence',
    'PaymentAlreadyMailed',
    'PaymentReturned',
    'PaymentDueDatePaydayConflict',
    'NoCreditCardProcessing',
    'PayingOnline',
    'Coronavirus'
]);

export type DelinquentReason = z.infer<typeof DelinquentReason>;

export const DelinquentReasonNames: Record<string, string> = {
    Unemployed: 'Unemployed',
    LossOfIncomeFinancialHardship: 'Loss of Income/Financial Hardship',
    Divorce: 'Divorce',
    DisputeWithClientDissatisfiedWithResort: 'Dispute with Client/Dissatisfied with Resort',
    RefusalToPay: 'Refusal to Pay',
    CustomerOversight: 'Customer Oversight',
    Medical: 'Medical',
    UnableToObtain: 'Unable to Obtain',
    ResearchCRFPending: 'Research/CRF Pending',
    Bankrupt: 'Bankrupt',
    Deceased: 'Deceased',
    Other: 'Other',
    CCCS: 'CCCS',
    SettlementPending: 'Settlement Pending',
    ReAgeProgram: 'Re-age Program',
    RefusedToState: 'Refused to State',
    NoCorrespondence: 'No Correspondence',
    PaymentAlreadyMailed: 'Payment Already Mailed',
    PaymentReturned: 'Payment Returned',
    PaymentDueDatePaydayConflict: 'Payment Due Date/Payday Conflict',
    NoCreditCardProcessing: 'No Card Processing',
    PayingOnline: 'Paying Online',
    Coronavirus: 'Coronavirus'
};

export const CallReason = z.enum([
    'NaturalDisaster',
    'PTOIssueProjectDelay',
    'DisputeWithInstallerSystemNotFunctional',
    'MakingPayment',
    'WebsiteQuestion',
    'UpdateOnExistingCase',
    'ConfusionWith1stPaymentDate',
    'CeaseAndDesist',
    'PromotionalFinancingQuestion',
    'HomeSaleOrRefinance',
    'LienRelatedQuestion',
    'PayoffQuote',
    'ExchangeQuestions',
    'POSSalesIssues',
    'ResalesInformation'
]);

export type CallReason = z.infer<typeof CallReason>;

export const CallReasonNames: Record<string, string> = {
    NaturalDisaster: 'Natural Disaster',
    PTOIssueProjectDelay: 'PTO Issue/Project Delay',
    DisputeWithInstallerSystemNotFunctional: 'Dispute with Installer/System Not Functional',
    MakingPayment: 'Making Payment',
    WebsiteQuestion: 'Website Question',
    UpdateOnExistingCase: 'Update on Existing Case',
    ConfusionWith1stPaymentDate: 'Confusion with 1st Payment Date',
    CeaseAndDesist: 'Cease and Desist',
    PromotionalFinancingQuestion: 'Promotional Financing Question',
    HomeSaleOrRefinance: 'Home Sale or Refinance',
    LienRelatedQuestion: 'Lien Related Question',
    PayoffQuote: 'Payoff Quote',
    ExchangeQuestions: 'Exchange Questions',
    POSSalesIssues: 'POS/Sales Issues',
    ResalesInformation: 'Resales Information'
};

export const BankType = z.enum(['Chase', 'WellsFargo', 'Nbaz', 'UsBank', 'MarriottCPS', 'Unknown']);
export type BankType = z.infer<typeof BankType>;
export const BankTypes: Array<BankType> = ['Chase', 'WellsFargo', 'Nbaz', 'UsBank', 'MarriottCPS', 'Unknown'];

export const BankTypeNames: Record<BankType, string> = {
    Unknown: 'Unknown',
    Chase: 'Chase',
    WellsFargo: 'Wells Fargo',
    Nbaz: 'National Bank of Arizona',
    UsBank: 'US Bank',
    MarriottCPS: 'Marriott CPS'
};

export const TreasurySummaryMethod = z.enum([
    'EBox',
    'Lockbox',
    'Ach',
    'AchReturn',
    'Check',
    'VisaMc',
    'Amex',
    'Discover',
    'UnknownCard',
    'Transfer'
]);
export type TreasurySummaryMethod = z.infer<typeof TreasurySummaryMethod>;

export const TreasurySummaryMethodNames: Record<TreasurySummaryMethod, string> = {
    EBox: 'EBox',
    Lockbox: 'Lockbox',
    Ach: 'ACH',
    AchReturn: 'ACH Return',
    Check: 'Check',
    VisaMc: 'Visa/Mastercard',
    Amex: 'American Express',
    Discover: 'Discover',
    UnknownCard: 'Unknown Card',
    Transfer: 'Transfer'
};

export const BankBalancingStatus = z.enum(['Unsettled', 'Exception', 'Settled']);
export type BankBalancingStatus = z.infer<typeof BankBalancingStatus>;

export const BankTransferApprovalStatus = z.enum(['Pending', 'Approved', 'Rejected']);
export type BankTransferApprovalStatus = z.infer<typeof BankTransferApprovalStatus>;

// Debit = positive collections (rest -> dest); Credit = negative refunds (dest -> rest).
export const TransferDirection = z.enum(['Debit', 'Credit']);
export type TransferDirection = z.infer<typeof TransferDirection>;

export const Frequency = z.enum([
    'Weekly',
    'Monthly',
    'Quarterly',
    'TwiceYearly',
    'Yearly',
    'EveryTwoYears',
    'EveryThreeYears',
    'NotReported'
]);

export type Frequency = z.infer<typeof Frequency>;

export const CreditCardType = z.enum(['Unknown', 'Visa', 'Mastercard', 'Discover', 'AmericanExpress']);
export type CreditCardType = z.infer<typeof CreditCardType>;

export const LenderBankSubscriptionType = z.enum(['Ach', 'Credit', 'Debit']);
export type LenderBankSubscriptionType = z.infer<typeof LenderBankSubscriptionType>;

export enum KnownCodes {
    Payment = 'Payment',
    Return = 'Return',
    Adjustment = 'Adjustment',
    Charge = 'Charge',
    Refund = 'Refund'
}

export enum KnownSubcodes {
    NEW = 'NEW',
    CNV = 'CNV',
    CHG = 'CHG',
    EXT = 'EXT',
    CAN = 'CAN',
    REI = 'REI',
    UAC = 'UAC',
    ADJ = 'ADJ',
    RFD = 'RFD',
    LCW = 'LCW',
    WOB = 'WOB',
    CHR = 'CHR',
    LCA = 'LCA',
    RTF = 'RTF',
    CFX = 'CFX',
    EFT = 'EFT',
    PMT = 'PMT',
    MMV = 'MMV',
    MMM = 'MMM',
    CMV = 'CMV',
    CMM = 'CMM',
    MDC = 'MDC',
    DCK = 'DCK',
    CDC = 'CDC',
    NSF = 'NSF',
    RT6 = 'RT6',
    RT0 = 'RT0',
    RT1 = 'RT1',
    RT3 = 'RT3',
    RT8 = 'RT8',
    RT4 = 'RT4',
    LBR = 'LBR',
    DUE = 'DUE'
}

export const DeliverabilityStatus = z.enum(['Deliverable', 'DoNotUse', 'Undeliverable']);
export type DeliverabilityStatus = z.infer<typeof DeliverabilityStatus>;

export const DeliverabilityStatusLabels: Record<DeliverabilityStatus, string> = {
    Deliverable: 'Deliverable',
    DoNotUse: 'Do Not Use',
    Undeliverable: 'Undeliverable'
};

export const CardType = z.enum(['Credit', 'Debit']);
export type CardType = z.infer<typeof CardType>;
